import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { DataGrid, itIT, GridToolbar, useGridApiRef } from '@mui/x-data-grid';
import { Box, Alert, Dialog, DialogTitle, DialogContent, DialogActions, Button, CircularProgress } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';

import Api from '../../Services/Api';
import Functions from '../../Services/Functions';
import ActionButton from '../ActionButton.js';
import { PAGE_SIZE, pageSizeOptions } from '../../Config/constants.js';

const REPAIR_LABELS = {
    repair_date:   "Data intervento",
    transfer_hours: "Ore viaggio",
    work_hours:    "Ore di lavoro",
    description:   "Descrizione dell'intervento",
    warranty:      "Garanzia",
    price:         "Prezzo manodopera",
    return_date:   "Data prossimo intervento",
    notes:         "Note di intervento",
};

const REPAIR_DATE_FIELDS = new Set(['repair_date', 'return_date']);

const VERIFICATION_SKIP = new Set([
    'id', 'appointment_id', 'last_editor_id', 'technician_id',
    'stamp_type_id', 'stamp_price', 'signature_tecnician', 'signature_customer',
    'created_at', 'updated_at', 'module_num',
]);

function formatValue(value, columnType, dataType) {
    if (value === null || value === undefined) return '-';
    if (columnType === 'tinyint(1)') return value ? 'Sì' : 'No';
    if (dataType === 'datetime') {
        const d = new Date(value);
        return d.toLocaleDateString('it-IT') + ' ' + d.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
    }
    if (dataType === 'date') {
        return new Date(value).toLocaleDateString('it-IT');
    }
    return String(value);
}

function InstallationWorkHistory() {
    const { installation } = useParams();
    const apiRef = useGridApiRef();

    const [rows, setRows] = useState([]);
    const [displayAlert, setDisplayAlert] = useState(0);
    const [verificationSchema, setVerificationSchema] = useState([]);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogLoading, setDialogLoading] = useState(false);
    const [dialogRecord, setDialogRecord] = useState(null);
    const [dialogTitle, setDialogTitle] = useState('');

    useEffect(() => {
        Api.get({ endpoint: 'verification/table/schema', contentType: 'application/json' })
            .then(response => {
                const labeled = response.data.fields.filter(
                    f => f.label !== `verifications.${f.name}` && !VERIFICATION_SKIP.has(f.name)
                );
                setVerificationSchema(labeled);
            })
            .catch(error => console.log(JSON.stringify(error)));
    }, []);  // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        try {
            Api.get({ endpoint: `installation/${installation}/work-history`, contentType: 'application/json' })
                .then(response => setRows(response.data.events))
                .catch(error => {
                    console.log(JSON.stringify(error));
                    setDisplayAlert(-1);
                });
        } catch (error) {
            console.log(JSON.stringify(error));
            setDisplayAlert(-1);
        }
    }, [installation]);  // eslint-disable-line react-hooks/exhaustive-deps

    const handleDownloadPdf = (row) => {
        const endpoint = row.record_type === 'repair'
            ? `export/generate-repair-pdf/${row.record_id}`
            : `export/pdf/verification/${row.record_id}`;
        const options = row.record_type === 'repair'
            ? { endpoint, responseType: 'blob' }
            : { endpoint, responseType: 'blob', axios: true };
        Api.get(options)
            .then(response => Functions.openBlob(response.data))
            .catch(error => console.log(JSON.stringify(error)));
    };

    const handleView = (row) => {
        setDialogTitle(row.appointment_type);
        setDialogRecord(null);
        setDialogLoading(true);
        setDialogOpen(true);

        const endpoint = row.record_type === 'repair'
            ? `repair/${row.record_id}`
            : `verification/${row.record_id}`;

        Api.get({ endpoint, contentType: 'application/json' })
            .then(response => {
                const data = row.record_type === 'repair'
                    ? { ...response.data.repair, _type: 'repair' }
                    : { ...response.data.verification, _type: 'verification' };
                setDialogRecord(data);
                setDialogLoading(false);
            })
            .catch(error => {
                console.log(JSON.stringify(error));
                setDialogLoading(false);
            });
    };

    const renderDialogContent = () => {
        if (dialogLoading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress /></Box>;
        if (!dialogRecord) return null;

        let entries;
        if (dialogRecord._type === 'repair') {
            entries = Object.entries(REPAIR_LABELS).map(([field, label]) => {
                const raw = dialogRecord[field];
                let value;
                if (raw === null || raw === undefined) {
                    value = '-';
                } else if (REPAIR_DATE_FIELDS.has(field)) {
                    value = new Date(raw).toLocaleDateString('it-IT');
                } else {
                    value = String(raw);
                }
                return { label, value };
            });
        } else {
            entries = verificationSchema.map(f => ({
                label: f.label,
                value: formatValue(dialogRecord[f.name], f.column_type, f.data_type),
            }));
        }

        return (
            <Box sx={{ pt: 1 }}>
                {entries.map(({ label, value }, i) => (
                    <Box key={i} sx={{ mb: 0.5 }}>
                        <b>{label}</b>: {value}
                    </Box>
                ))}
            </Box>
        );
    };

    const columns = [
        {
            field: 'appointment_type',
            headerName: 'Tipologia',
            flex: 1.5,
        },
        {
            field: 'event_date',
            headerName: 'Data',
            flex: 1.5,
            type: 'date',
            valueGetter: (params) => params.row?.event_date ? new Date(params.row.event_date) : null,
            valueFormatter: (params) => params.value ? new Date(params.value).toLocaleDateString('it-IT') : '',
        },
        {
            field: 'technician',
            headerName: 'Tecnico',
            flex: 2,
        },
        {
            field: 'stamp_id',
            headerName: 'Bollino/numero',
            flex: 1.5,
            valueGetter: (params) => {
                const row = params.row;
                if (row.record_type === 'repair') return row.progressive ?? '';
                if (row.appointment_type === 'Manutenzione') return 'M';
                return row.stamp_id ?? '';
            },
        },
        {
            field: 'prescriptions',
            headerName: 'Descrizione / Prescrizioni',
            flex: 3,
            sortable: false,
            valueGetter: (params) => params.row?.prescriptions || params.row?.description || '',
        },
        {
            field: 'azioni',
            headerName: 'Azioni',
            width: 100,
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            renderCell: (params) => (
                <>
                    <ActionButton title="Visualizza dettagli" onClick={() => handleView(params.row)}>
                        <VisibilityIcon />
                    </ActionButton>
                    <ActionButton title={params.row.is_closed ? "Scarica PDF" : "PDF non disponibile: appuntamento non chiuso"} disabled={!params.row.is_closed} onClick={() => handleDownloadPdf(params.row)}>
                        <PictureAsPdfIcon />
                    </ActionButton>
                </>
            ),
        },
    ];

    const theme = createTheme(
        { palette: { primary: { main: '#11cb5f' } } },
        itIT,
    );

    return (
        <>
            <h5>Storico interventi</h5>
            <Box sx={{ width: '100%', minWidth: '800px' }}>
                <ThemeProvider theme={theme}>
                    <DataGrid
                        sx={{ minHeight: '300px' }}
                        rows={rows}
                        columns={columns}
                        initialState={{
                            pagination: { paginationModel: { pageSize: PAGE_SIZE } },
                            sorting: { sortModel: [{ field: 'event_date', sort: 'desc' }] },
                        }}
                        apiRef={apiRef}
                        slots={{ toolbar: GridToolbar }}
                        slotProps={{ columnsPanel: { disableHideAllButton: true } }}
                        pageSizeOptions={pageSizeOptions}
                        disableRowSelectionOnClick
                    />
                </ThemeProvider>
                {displayAlert === -1 && (
                    <Alert severity="error">Si è verificato un errore imprevisto. Riprovare più tardi</Alert>
                )}
            </Box>

            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth scroll="paper">
                <DialogTitle>{dialogTitle}</DialogTitle>
                <DialogContent dividers>
                    {renderDialogContent()}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogOpen(false)}>Chiudi</Button>
                </DialogActions>
            </Dialog>
        </>
    );
}

export default InstallationWorkHistory;
